import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  MessageSquare, Phone, Mail, Users, Video, Send, FileText, MapPin,
  Plus, Search, Filter, Calendar, Clock, AlertCircle, CheckCircle2,
  ChevronRight, X, RefreshCw, ExternalLink, Tag, User, Building2,
  Paperclip, Trash2, Image, FileUp
} from "lucide-react";
import { toast } from "sonner";

const COMM_TYPES = [
  { value: "call", label: "Phone Call", icon: Phone, color: "bg-blue-500" },
  { value: "email", label: "Email", icon: Mail, color: "bg-purple-500" },
  { value: "meeting", label: "Meeting", icon: Users, color: "bg-green-500" },
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare, color: "bg-emerald-500" },
  { value: "sms", label: "SMS", icon: Send, color: "bg-cyan-500" },
  { value: "site_visit", label: "Site Visit", icon: MapPin, color: "bg-orange-500" },
  { value: "letter", label: "Letter", icon: FileText, color: "bg-slate-500" },
  { value: "other", label: "Other", icon: MessageSquare, color: "bg-gray-500" }
];

const STAKEHOLDER_TYPES = [
  { value: "discom", label: "DISCOM" },
  { value: "gmr", label: "GMR" },
  { value: "contractor", label: "Contractor" },
  { value: "consumer", label: "Consumer" },
  { value: "internal", label: "Internal" },
  { value: "vendor", label: "Vendor" },
  { value: "government", label: "Government" },
  { value: "other", label: "Other" }
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-slate-400" },
  { value: "normal", label: "Normal", color: "bg-blue-500" },
  { value: "high", label: "High", color: "bg-orange-500" },
  { value: "urgent", label: "Urgent", color: "bg-red-500" }
];

export default function CommunicationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingFollowups, setPendingFollowups] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    comm_type: "",
    stakeholder_type: "",
    priority: "",
    status: "",
    requires_followup: "",
    search: ""
  });

  const [form, setForm] = useState({
    comm_type: "call",
    direction: "outbound",
    subject: "",
    description: "",
    stakeholder_type: "discom",
    stakeholder_name: "",
    stakeholder_contact: "",
    stakeholder_designation: "",
    priority: "normal",
    dt_id: "",
    feeder_code: "",
    comm_datetime: new Date().toISOString().slice(0, 16),
    duration_minutes: "",
    location: "",
    participants: "",
    requires_followup: false,
    followup_date: "",
    followup_notes: "",
    tags: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      
      const [itemsRes, statsRes, followupsRes] = await Promise.all([
        api.get("/communications", { params }),
        api.get("/communications/stats"),
        api.get("/communications/pending-followups")
      ]);
      
      setItems(itemsRes.data.items || []);
      setTotal(itemsRes.data.total || 0);
      setStats(statsRes.data);
      setPendingFollowups(followupsRes.data);
    } catch (e) {
      toast.error("Failed to load communications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/communications/upload-attachment", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        uploaded.push({ url: res.data.url, filename: res.data.filename, content_type: res.data.content_type, size: res.data.size });
      }
      setAttachments(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "File upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.comm_type) { toast.error("Communication type is required"); return; }
    if (!form.subject?.trim()) { toast.error("Subject is required"); return; }
    if (!form.description?.trim()) { toast.error("Description is required"); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        participants: form.participants ? form.participants.split(",").map(p => p.trim()) : [],
        tags: form.tags ? form.tags.split(",").map(t => t.trim()) : [],
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
        attachments: attachments.map(a => a.url)
      };
      
      await api.post("/communications", payload);
      toast.success("Communication logged successfully");
      setShowCreate(false);
      setAttachments([]);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create communication");
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setForm({
      comm_type: "call",
      direction: "outbound",
      subject: "",
      description: "",
      stakeholder_type: "discom",
      stakeholder_name: "",
      stakeholder_contact: "",
      stakeholder_designation: "",
      priority: "normal",
      dt_id: "",
      feeder_code: "",
      comm_datetime: new Date().toISOString().slice(0, 16),
      duration_minutes: "",
      location: "",
      participants: "",
      requires_followup: false,
      followup_date: "",
      followup_notes: "",
      tags: ""
    });
  };

  const getTypeConfig = (type) => COMM_TYPES.find(t => t.value === type) || COMM_TYPES[7];
  const getPriorityConfig = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
      });
    } catch { return dateStr; }
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="communications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Communication Log</h1>
          <p className="text-slate-500 text-sm">{total} communications logged</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600"
            data-testid="log-comm-btn"
          >
            <Plus size={16} />Log Communication
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Today</div>
            <div className="text-2xl font-black text-orange-500">{stats.today_count}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Calls</div>
            <div className="text-2xl font-black text-blue-500">{stats.by_type?.call || 0}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Emails</div>
            <div className="text-2xl font-black text-purple-500">{stats.by_type?.email || 0}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Meetings</div>
            <div className="text-2xl font-black text-green-500">{stats.by_type?.meeting || 0}</div>
          </div>
          <div className="bg-white border border-red-200 rounded-sm p-3">
            <div className="text-xs text-red-500 uppercase font-bold">Pending F/U</div>
            <div className="text-2xl font-black text-red-500">{stats.pending_followups}</div>
          </div>
        </div>
      )}

      {/* Pending Follow-ups Alert */}
      {pendingFollowups && pendingFollowups.overdue?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-bold text-red-700">Overdue Follow-ups</div>
            <div className="text-sm text-red-600">{pendingFollowups.overdue.length} communications require immediate attention</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search communications..."
            className="field-input pl-9 h-9 text-xs"
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <select className="field-select h-9 text-xs" value={filters.comm_type} onChange={e => setFilters({ ...filters, comm_type: e.target.value })}>
          <option value="">All Types</option>
          {COMM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select className="field-select h-9 text-xs" value={filters.stakeholder_type} onChange={e => setFilters({ ...filters, stakeholder_type: e.target.value })}>
          <option value="">All Stakeholders</option>
          {STAKEHOLDER_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="field-select h-9 text-xs" value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select className="field-select h-9 text-xs" value={filters.requires_followup} onChange={e => setFilters({ ...filters, requires_followup: e.target.value })}>
          <option value="">All</option>
          <option value="true">Needs Follow-up</option>
          <option value="false">No Follow-up</option>
        </select>
      </div>

      {/* Communications List */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
            <p className="text-slate-500">Loading communications...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <MessageSquare size={32} className="mx-auto mb-2" />
            <p>No communications found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map(item => {
              const typeConfig = getTypeConfig(item.comm_type);
              const priorityConfig = getPriorityConfig(item.priority);
              const TypeIcon = typeConfig.icon;
              
              return (
                <div
                  key={item.id}
                  className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => setShowDetail(item)}
                >
                  <div className="flex items-start gap-3">
                    {/* Type Icon */}
                    <div className={`w-10 h-10 rounded-full ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                      <TypeIcon size={18} className="text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-slate-800 text-sm truncate">{item.subject}</h3>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Building2 size={12} />
                              {item.stakeholder_name || item.stakeholder_type}
                            </span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatDateTime(item.comm_datetime)}
                            </span>
                            {item.direction && (
                              <>
                                <span>•</span>
                                <span className={item.direction === "inbound" ? "text-green-600" : "text-blue-600"}>
                                  {item.direction === "inbound" ? "↓ In" : item.direction === "outbound" ? "↑ Out" : "↔ Int"}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.requires_followup && item.status !== "resolved" && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold">
                              F/U
                            </span>
                          )}
                          <span className={`w-2 h-2 rounded-full ${priorityConfig.color}`} title={priorityConfig.label} />
                          <ChevronRight size={16} className="text-slate-400" />
                        </div>
                      </div>
                      
                      {/* Description Preview */}
                      <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.description}</p>
                      
                      {/* Attachments indicator + Tags */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {item.attachments?.length > 0 && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-medium">
                            <Paperclip size={10} />{item.attachments.length} file{item.attachments.length > 1 ? "s" : ""}
                          </span>
                        )}
                        {item.tags?.slice(0, 3).map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px]">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Communication Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Log Communication</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {/* Type Selection */}
              <div>
                <label className="field-label">Type *</label>
                <div className="grid grid-cols-4 gap-2">
                  {COMM_TYPES.map(t => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm({ ...form, comm_type: t.value })}
                        className={`p-3 rounded-sm border-2 flex flex-col items-center gap-1 transition-colors ${
                          form.comm_type === t.value ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <Icon size={18} className={form.comm_type === t.value ? "text-orange-500" : "text-slate-500"} />
                        <span className="text-xs font-medium">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Direction */}
                <div>
                  <label className="field-label">Direction *</label>
                  <select className="field-select" required value={form.direction} onChange={e => setForm({ ...form, direction: e.target.value })}>
                    <option value="outbound">Outbound (We called/sent)</option>
                    <option value="inbound">Inbound (They called/sent)</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label className="field-label">Priority</label>
                  <select className="field-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="field-label">Subject *</label>
                <input className="field-input" required placeholder="Brief subject of the communication" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
              </div>

              {/* Stakeholder Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Stakeholder Type *</label>
                  <select className="field-select" required value={form.stakeholder_type} onChange={e => setForm({ ...form, stakeholder_type: e.target.value })}>
                    {STAKEHOLDER_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Stakeholder Name</label>
                  <input className="field-input" placeholder="Person/Org name" value={form.stakeholder_name} onChange={e => setForm({ ...form, stakeholder_name: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Contact (Phone/Email)</label>
                  <input className="field-input" placeholder="9876543210" value={form.stakeholder_contact} onChange={e => setForm({ ...form, stakeholder_contact: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Designation</label>
                  <input className="field-input" placeholder="JE, AE, etc." value={form.stakeholder_designation} onChange={e => setForm({ ...form, stakeholder_designation: e.target.value })} />
                </div>
              </div>

              {/* Date/Time & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Date & Time</label>
                  <input type="datetime-local" className="field-input" value={form.comm_datetime} onChange={e => setForm({ ...form, comm_datetime: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Duration (minutes)</label>
                  <input type="number" className="field-input" placeholder="15" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="field-label">Description / Notes *</label>
                <textarea className="field-input" rows={4} required placeholder="Detailed notes about the communication..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>

              {/* Follow-up */}
              <div className="bg-slate-50 rounded-sm p-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.requires_followup} onChange={e => setForm({ ...form, requires_followup: e.target.checked })} className="rounded" />
                  <span className="text-sm font-medium">Requires Follow-up</span>
                </label>
                
                {form.requires_followup && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Follow-up Date</label>
                      <input type="date" className="field-input" value={form.followup_date} onChange={e => setForm({ ...form, followup_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="field-label">Follow-up Notes</label>
                      <input className="field-input" placeholder="What to follow up on" value={form.followup_notes} onChange={e => setForm({ ...form, followup_notes: e.target.value })} />
                    </div>
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div>
                <label className="field-label">Attachments</label>
                <div className="border-2 border-dashed border-slate-200 rounded-sm p-3">
                  <label className="flex items-center justify-center gap-2 cursor-pointer py-2 hover:bg-slate-50 rounded transition-colors" data-testid="comm-attach-btn">
                    <input type="file" multiple className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                    {uploading ? (
                      <><RefreshCw size={16} className="animate-spin text-orange-500" /><span className="text-sm text-orange-500 font-medium">Uploading...</span></>
                    ) : (
                      <><Paperclip size={16} className="text-slate-400" /><span className="text-sm text-slate-500">Click to attach files (screenshots, call logs, WhatsApp chats, PDFs...)</span></>
                    )}
                  </label>
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                      {attachments.map((att, i) => {
                        const isImage = att.content_type?.startsWith("image/");
                        return (
                          <div key={i} className="flex items-center gap-2 bg-slate-50 rounded p-2">
                            {isImage ? <Image size={14} className="text-blue-500 flex-shrink-0" /> : <FileText size={14} className="text-orange-500 flex-shrink-0" />}
                            <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate flex-1">{att.filename}</a>
                            <span className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(0)} KB</span>
                            <button type="button" onClick={() => removeAttachment(i)} className="p-1 hover:bg-red-100 rounded" data-testid={`remove-attach-${i}`}>
                              <Trash2 size={12} className="text-red-400" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Tags (comma separated) */}
              <div>
                <label className="field-label">Tags (comma separated)</label>
                <input className="field-input" placeholder="shutdown, complaint, approval" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm font-semibold text-slate-600">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold hover:bg-orange-600 disabled:opacity-60">
                  {creating ? "Saving..." : "Log Communication"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <CommunicationDetailModal
          communication={showDetail}
          onClose={() => setShowDetail(null)}
          onUpdate={fetchData}
        />
      )}
    </div>
  );
}

// Detail Modal Component
function CommunicationDetailModal({ communication, onClose, onUpdate }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowup, setShowFollowup] = useState(false);
  const [followupForm, setFollowupForm] = useState({ notes: "", action_taken: "", next_followup_date: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [communication.id]);

  const fetchDetail = async () => {
    try {
      const res = await api.get(`/communications/${communication.id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/communications/${communication.id}/followup`, followupForm);
      toast.success("Follow-up added");
      setShowFollowup(false);
      setFollowupForm({ notes: "", action_taken: "", next_followup_date: "" });
      fetchDetail();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add follow-up");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async () => {
    const resolution = prompt("Enter resolution notes:");
    if (!resolution) return;
    
    try {
      await api.post(`/communications/${communication.id}/resolve?resolution=${encodeURIComponent(resolution)}`);
      toast.success("Communication resolved");
      fetchDetail();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve");
    }
  };

  const typeConfig = COMM_TYPES.find(t => t.value === communication.comm_type) || COMM_TYPES[7];
  const TypeIcon = typeConfig.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full ${typeConfig.color} flex items-center justify-center`}>
                <TypeIcon size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{communication.subject}</h3>
                <div className="text-sm text-slate-500">
                  {communication.stakeholder_name || communication.stakeholder_type} • {new Date(communication.comm_datetime).toLocaleDateString()}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw size={24} className="mx-auto animate-spin text-orange-500" />
          </div>
        ) : detail && (
          <div className="p-6 space-y-6">
            {/* Status Badge */}
            <div className="flex items-center gap-2">
              {detail.status === "resolved" ? (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold flex items-center gap-1">
                  <CheckCircle2 size={14} /> Resolved
                </span>
              ) : detail.requires_followup ? (
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold flex items-center gap-1">
                  <AlertCircle size={14} /> Needs Follow-up
                </span>
              ) : (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">Open</span>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Description</label>
              <p className="mt-1 text-slate-700 whitespace-pre-wrap">{detail.description}</p>
            </div>

            {/* Stakeholder Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Stakeholder</label>
                <p className="mt-1 text-slate-700">{detail.stakeholder_name || "N/A"}</p>
                <p className="text-sm text-slate-500">{detail.stakeholder_designation}</p>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Contact</label>
                <p className="mt-1 text-slate-700">{detail.stakeholder_contact || "N/A"}</p>
              </div>
            </div>

            {/* Attachments */}
            {detail.attachments?.length > 0 && (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Attachments</label>
                <div className="grid grid-cols-2 gap-2">
                  {detail.attachments.map((url, i) => {
                    const fname = url.split("/").pop();
                    const isImg = /\.(jpg|jpeg|png|webp|heic)$/i.test(fname);
                    return (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded hover:bg-slate-100 transition-colors" data-testid={`attachment-${i}`}>
                        {isImg ? <Image size={14} className="text-blue-500 flex-shrink-0" /> : <FileText size={14} className="text-orange-500 flex-shrink-0" />}
                        <span className="text-xs text-blue-600 truncate">{fname}</span>
                        <ExternalLink size={12} className="text-slate-400 flex-shrink-0 ml-auto" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Follow-up History */}
            {detail.followup_history?.length > 0 && (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Follow-up History</label>
                <div className="space-y-3">
                  {detail.followup_history.map((fu, i) => (
                    <div key={i} className="border-l-2 border-orange-300 pl-3 py-2">
                      <div className="text-sm font-medium text-slate-800">{fu.action_taken}</div>
                      <div className="text-xs text-slate-500 mt-1">{fu.notes}</div>
                      <div className="text-xs text-slate-400 mt-1">
                        {new Date(fu.created_at).toLocaleString()} • {fu.created_by_name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Follow-up Form */}
            {showFollowup && (
              <form onSubmit={handleAddFollowup} className="bg-slate-50 rounded-sm p-4 space-y-3">
                <div>
                  <label className="field-label">Action Taken *</label>
                  <input className="field-input" required placeholder="What was done?" value={followupForm.action_taken} onChange={e => setFollowupForm({ ...followupForm, action_taken: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Notes</label>
                  <textarea className="field-input" rows={2} placeholder="Additional notes..." value={followupForm.notes} onChange={e => setFollowupForm({ ...followupForm, notes: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Next Follow-up Date (leave empty to resolve)</label>
                  <input type="date" className="field-input" value={followupForm.next_followup_date} onChange={e => setFollowupForm({ ...followupForm, next_followup_date: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowFollowup(false)} className="px-4 py-2 border border-slate-200 rounded-sm text-sm">Cancel</button>
                  <button type="submit" disabled={submitting} className="px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold disabled:opacity-60">
                    {submitting ? "Saving..." : "Add Follow-up"}
                  </button>
                </div>
              </form>
            )}

            {/* Actions */}
            {detail.status !== "resolved" && (
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                {!showFollowup && (
                  <>
                    <button onClick={() => setShowFollowup(true)} className="flex-1 px-4 py-2 border-2 border-orange-500 text-orange-600 rounded-sm font-bold text-sm">
                      Add Follow-up
                    </button>
                    <button onClick={handleResolve} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-sm font-bold text-sm">
                      Mark Resolved
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Resolution */}
            {detail.resolution && (
              <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                <label className="text-xs font-bold text-green-700 uppercase">Resolution</label>
                <p className="mt-1 text-green-800">{detail.resolution}</p>
                <p className="text-xs text-green-600 mt-2">Resolved on {new Date(detail.resolved_at).toLocaleString()}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
